import { Dispatch, SetStateAction, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Nav from 'components/common/nav/Nav';
import SubNav from 'components/common/nav/SubNav';
import PeriodNotice from '../../pages/chart/PeriodNotice';
import classNames from 'classnames';
import styles from 'styles/pages/chart/ChartLayout.module.scss';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { ChartInfo } from 'constants/chart/common/chartInfo';

type TProps = {
  category: string | undefined;
  chartType: number;
  children: React.ReactNode;
  tabList: number[];
  activeTabIndex: number;
  setActiveTabIndex: Dispatch<SetStateAction<number>>;
  submitEndDate: string;
  pentool?: boolean;
  isReadOnly: boolean;
  evaluationChartId?: number;
};

const ChartLayout = ({
  category,
  chartType,
  children,
  tabList,
  activeTabIndex,
  setActiveTabIndex,
  submitEndDate,
  pentool,
  isReadOnly,
  evaluationChartId,
}: TProps) => {
  const chartInfo = ChartInfo.find((info) => info.type === chartType);

  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const handleTabClick = (index: number) => {
    setActiveTabIndex(index);
    query.set('evaluationChartId', evaluationChartId?.toString() || '');
    query.set('tab', index.toString());
    navigate({
      search: query.toString(),
    });
  };

  useEffect(() => {
    if (category === 'feedback') {
      const chartRoute = ChartInfo.find((chart) => chart.type === chartType)
        ?.route;
      if (evaluationChartId) {
        query.set('evaluationChartId', evaluationChartId.toString());
        navigate({
          pathname: `/feedback${chartRoute}/${evaluationChartId}`,
          search: query.toString(),
        });
      }
    }
  }, [evaluationChartId]);

  return (
    <div style={{ minWidth: '1280px', position: 'relative' }}>
      <Nav />
      {category !== 'practice' && <SubNav mode={category} />}
      <div
        style={{
          backgroundColor: STYLES_VARIABLES.PURPLE_05,
          padding: '48px 0',
        }}
      >
        <div className={styles.layout}>
          {(category === 'evaluation' || category === 'feedback') && (
            <div className="flex chart_tab">
              {tabList.includes(0) && (
                <button
                  type="button"
                  className={classNames(styles.tab, {
                    [styles.tab_active]: activeTabIndex === 0,
                  })}
                  onClick={() => handleTabClick(0)}
                >
                  <span className={styles.text}>1차 내원</span>
                </button>
              )}
              {tabList.includes(1) && (
                <button
                  type="button"
                  className={classNames(styles.tab, {
                    [styles.tab_active]: activeTabIndex === 1,
                  })}
                  onClick={() => handleTabClick(1)}
                >
                  <span className={styles.text}>1차 평가</span>
                </button>
              )}
              {tabList.includes(2) && (
                <button
                  type="button"
                  className={classNames(styles.tab, {
                    [styles.tab_active]: activeTabIndex === 2,
                  })}
                  onClick={() => handleTabClick(2)}
                >
                  <span className={styles.text}>2차 내원</span>
                </button>
              )}
              {tabList.includes(3) && (
                <button
                  type="button"
                  className={classNames(styles.tab, {
                    [styles.tab_active]: activeTabIndex === 3,
                  })}
                  onClick={() => handleTabClick(3)}
                >
                  <span className={styles.text}>2차 평가</span>
                </button>
              )}
            </div>
          )}
          <div
            id="chartArea"
            className={classNames('chart_bg', {
              ['chart_bg_pentool']: category === 'feedback' || pentool,
            })}
          >
            <div
              className={classNames(
                'flex',
                'justify_between',
                styles.chart_title,
                {
                  [styles.title_pentool]: category === 'feedback' || pentool,
                }
              )}
            >
              <div>
                <h1 className="sub_nav_title">{chartInfo?.ko || ''}</h1>
                <p>({chartInfo?.en || ''})</p>
              </div>
              {category === 'evaluation' &&
                [0, 2].includes(activeTabIndex) &&
                location.pathname.includes('register') &&
                !isReadOnly && <PeriodNotice date={submitEndDate} />}
            </div>
            <div className="flex flex_column">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartLayout;
